import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Settings2, Users, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { PackageOfferingDto } from "@shared/types/PackageOfferingDto";

interface SolutionTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPackage: PackageOfferingDto | null;
}

export function SolutionTypeDialog({
  open,
  onOpenChange,
  selectedPackage,
}: SolutionTypeDialogProps) {
  const navigate = useNavigate();

  const handleConfigure = () => {
    if (selectedPackage) {
      onOpenChange(false);
      navigate(`/configurator/${selectedPackage.id}`);
    }
  };

  const handleSchedule = () => {
    onOpenChange(false);
    navigate("/meetings");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>How would you like to proceed?</DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-6 h-full flex flex-col hover:border-primary/50 transition-colors cursor-pointer group">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Settings2 className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Self-Guided</h3>
              <p className="text-sm text-muted-foreground flex-1 mb-4">
                Configure your solution at your own pace. Define requirements,
                milestones, and preferences through our step-by-step wizard.
              </p>
              <Button
                onClick={handleConfigure}
                className="w-full group-hover:bg-primary"
              >
                Configure
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="p-6 h-full flex flex-col hover:border-primary/50 transition-colors cursor-pointer group">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Assisted Guidance</h3>
              <p className="text-sm text-muted-foreground flex-1 mb-4">
                Work with me to define your solution. I'll guide you through the
                process in a consultation meeting.
              </p>
              <Button
                onClick={handleSchedule}
                variant="outline"
                className="w-full group-hover:border-emerald-500 group-hover:text-emerald-500"
              >
                Schedule
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Card>
          </motion.div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
